/**
 * 上传前的图片压缩与格式统一。
 *
 * 仓库图片约定为 webp（体积比 png 小一个量级，质量对齐 cwebp -q 88），
 * 上传链路是 base64 JSON（30s 超时、后端 50MB 上限），手机原图/设计稿直传
 * 容易超时超限，所以在浏览器端先缩放到目标边长并转码 webp 再上传。
 */

/** 与后端 /uploads/images 的 MIME 白名单一致，其余类型不做处理交后端校验。 */
const COMPRESSIBLE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

export interface ImageCompressOptions {
  /** 最长边上限，超过按比例缩小（不放大小图）。 */
  maxDimension: number;
  /** 0-1 转码质量，对齐 cwebp -q 88 约定取 0.88。 */
  quality: number;
}

export interface ScaledSize {
  width: number;
  height: number;
}

export function computeScaledSize(width: number, height: number, maxDimension: number): ScaledSize {
  if (width <= maxDimension && height <= maxDimension) {
    return { width, height };
  }
  const ratio = maxDimension / Math.max(width, height);
  return {
    width: Math.max(1, Math.round(width * ratio)),
    height: Math.max(1, Math.round(height * ratio)),
  };
}

/**
 * 缩放并转码为 webp；任一步骤不可用（解码失败、canvas 不支持等）时回退原文件，
 * 由后端的类型/大小校验兜底，不阻断上传。
 */
export async function compressImageFile(file: File, options: ImageCompressOptions): Promise<File> {
  if (!COMPRESSIBLE_MIME_TYPES.has(file.type)) {
    return file;
  }

  try {
    const bitmap = await loadImageSource(file);
    const scaled = computeScaledSize(bitmap.width, bitmap.height, options.maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = scaled.width;
    canvas.height = scaled.height;
    const context = canvas.getContext('2d');
    if (!context) {
      return file;
    }
    context.drawImage(bitmap.source, 0, 0, scaled.width, scaled.height);
    bitmap.release();

    const blob = await canvasToBlob(canvas, 'image/webp', options.quality);
    if (!blob || blob.size === 0) {
      return file;
    }

    const wasScaledDown = scaled.width !== bitmap.width || scaled.height !== bitmap.height;
    // 尺寸没缩、转码反而更大（源图本就高度优化）时保留原图
    if (!wasScaledDown && blob.size >= file.size) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, blob.type), { type: blob.type });
  } catch {
    return file;
  }
}

interface LoadedImageSource {
  source: CanvasImageSource;
  width: number;
  height: number;
  release: () => void;
}

async function loadImageSource(file: File): Promise<LoadedImageSource> {
  // 仅依赖 createImageBitmap（现代浏览器均支持）；不可用的环境（如测试 DOM）
  // 直接放弃压缩用原图，避免 new Image() 在不解码图片的环境里 onload 永不触发。
  if (typeof createImageBitmap !== 'function') {
    throw new Error('当前环境不支持图片压缩');
  }
  const bitmap = await createImageBitmap(file);
  return {
    source: bitmap,
    width: bitmap.width,
    height: bitmap.height,
    release: () => bitmap.close(),
  };
}

function canvasToBlob(canvas: HTMLCanvasElement, mimeType: string, quality: number) {
  return new Promise<Blob | null>((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeType, quality);
  });
}

function replaceExtension(fileName: string, mimeType: string) {
  // 浏览器不支持 webp 编码时 toBlob 会回退 png，扩展名跟随实际类型
  const extension = mimeType === 'image/webp' ? 'webp' : mimeType === 'image/png' ? 'png' : 'jpg';
  const baseName = fileName.replace(/\.[^.]*$/, '') || 'image';
  return `${baseName}.${extension}`;
}
